import { useRef } from 'react';
import type { KeyboardEvent } from 'react';

export interface ServerTabItem {
  /** An account id. */
  id: number;
  /** The account's world name (falls back to its worldSlug if the worlds catalog lookup misses). */
  label: string;
  /** The account's own label, shown as microtext — disambiguates two accounts on the same world. */
  microtext?: string;
}

interface ServerTabStripProps {
  tabs: ServerTabItem[];
  selectedId: number;
  onSelect: (id: number) => void;
  /**
   * DOM id namespace for the tab buttons + the `aria-controls` target (REV8: `/legion` reuses
   * this component and needs its own ids rather than colliding with `/links`' `links-tab-*` /
   * `links-tabpanel`). Defaults to `links` so the original `/links` call site is unchanged.
   */
  idPrefix?: string;
}

/**
 * `/links`' account tab strip (REV3 spec) — one tab per account, labeled by its world, replacing
 * the old account `<select>`. Deliberately a standalone component rather than importing
 * `components/bosses/CharacterTabStrip` (the REV3 spec explicitly keeps this route off files
 * another concurrent session owns) — but it reproduces that component's "PowerShell-terminal-style
 * tab row" visual recipe (docs/DESIGN.md) exactly: flat inactive tabs on `--bg`, the active tab
 * lifted to `--bg-elevated` with a 2px `--accent` top border, real `role="tablist"` semantics with
 * Left/Right/Home/End moving focus and selection together.
 */
export function ServerTabStrip({ tabs, selectedId, onSelect, idPrefix = 'links' }: ServerTabStripProps) {
  const tabRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

  const focusAndSelect = (id: number) => {
    onSelect(id);
    tabRefs.current.get(id)?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (tabs.length === 0) {
      return;
    }
    let target: ServerTabItem | undefined;
    if (event.key === 'ArrowRight') {
      target = tabs[(index + 1) % tabs.length];
    } else if (event.key === 'ArrowLeft') {
      target = tabs[(index - 1 + tabs.length) % tabs.length];
    } else if (event.key === 'Home') {
      target = tabs[0];
    } else if (event.key === 'End') {
      target = tabs[tabs.length - 1];
    }
    if (target) {
      event.preventDefault();
      focusAndSelect(target.id);
    }
  };

  return (
    <div
      role="tablist"
      aria-label="Server"
      className="flex gap-1 overflow-x-auto border-b border-border"
      style={{
        // Edge fade (docs/DESIGN.md tab-strip recipe) so a scrollable overflow reads as
        // continuing off-screen rather than abruptly clipping the last visible tab.
        maskImage: 'linear-gradient(to right, transparent 0, black 12px, black calc(100% - 12px), transparent 100%)',
        WebkitMaskImage:
          'linear-gradient(to right, transparent 0, black 12px, black calc(100% - 12px), transparent 100%)',
      }}
    >
      {tabs.map((tab, index) => {
        const isSelected = tab.id === selectedId;
        return (
          <button
            key={tab.id}
            ref={(el) => {
              if (el) {
                tabRefs.current.set(tab.id, el);
              } else {
                tabRefs.current.delete(tab.id);
              }
            }}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${tab.id}`}
            aria-selected={isSelected}
            aria-controls={`${idPrefix}-tabpanel`}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => onSelect(tab.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={[
              'flex min-h-tap shrink-0 flex-col items-start justify-center gap-0.5 whitespace-nowrap border-t-2 px-4 py-1.5 text-left transition-colors',
              isSelected
                ? 'border-t-accent bg-bg-elevated text-text'
                : 'border-t-transparent bg-bg text-text-muted hover:text-text',
            ].join(' ')}
          >
            <span className="text-sm font-semibold">{tab.label}</span>
            {tab.microtext && <span className="text-[0.65rem] text-text-faint">{tab.microtext}</span>}
          </button>
        );
      })}
    </div>
  );
}
